import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import moment from 'moment'
import DateRangeRoundedIcon from '@mui/icons-material/DateRangeRounded'
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded'

const FMT = 'YYYY-MM-DD'

const PRESETS = [
  { key: 'all', label: 'All time' },
  { key: 'last_30_days', label: 'Last 30 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'last_quarter', label: 'Last quarter' },
  { key: 'this_year', label: 'This year' },
  { key: 'last_year', label: 'Last year' },
  { key: 'last_12_months', label: 'Last 12 months' },
]

const niceDate = value => moment(value, FMT).format('D MMM YYYY')

export const resolvePeriod = (value = {}) => {
  const now = moment()
  const range = (start, end) => ({ from: start.format(FMT), to: end.format(FMT) })

  switch (value.preset) {
    case 'last_30_days': return { ...range(now.clone().subtract(29, 'days'), now.clone()), label: 'Last 30 days' }
    case 'this_month': return { ...range(now.clone().startOf('month'), now.clone().endOf('month')), label: now.format('MMMM YYYY') }
    case 'last_month': {
      const m = now.clone().subtract(1, 'month')
      return { ...range(m.clone().startOf('month'), m.clone().endOf('month')), label: m.format('MMMM YYYY') }
    }
    case 'this_quarter': return { ...range(now.clone().startOf('quarter'), now.clone().endOf('quarter')), label: `Q${now.quarter()} ${now.year()}` }
    case 'last_quarter': {
      const q = now.clone().subtract(1, 'quarter')
      return { ...range(q.clone().startOf('quarter'), q.clone().endOf('quarter')), label: `Q${q.quarter()} ${q.year()}` }
    }
    case 'this_year': return { ...range(now.clone().startOf('year'), now.clone().endOf('year')), label: String(now.year()) }
    case 'last_year': {
      const y = now.clone().subtract(1, 'year')
      return { ...range(y.clone().startOf('year'), y.clone().endOf('year')), label: String(y.year()) }
    }
    case 'last_12_months': return { ...range(now.clone().subtract(12, 'months').add(1, 'day'), now.clone()), label: 'Last 12 months' }
    case 'custom': {
      if (!value.from && !value.to) return { from: null, to: null, label: 'All time' }
      const from = value.from || value.to
      const to = value.to || value.from
      const [a, b] = moment(from, FMT).isAfter(moment(to, FMT)) ? [to, from] : [from, to]
      return { from: a, to: b, label: a === b ? niceDate(a) : `${niceDate(a)} to ${niceDate(b)}` }
    }
    default: return { from: null, to: null, label: 'All time' }
  }
}

const ReportPeriodPicker = ({ value, onChange, allowAllTime = true, label = 'Period' }) => {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState({ from: value?.from || '', to: value?.to || '' })
  const ref = useRef(null)
  const menuRef = useRef(null)
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  const resolved = resolvePeriod(value)
  const presets = allowAllTime ? PRESETS : PRESETS.filter(p => p.key !== 'all')

  useEffect(() => {
    if (!open) return
    setDraft({ from: resolved.from || '', to: resolved.to || '' })
    const onDown = e => {
      const inside = (ref.current && ref.current.contains(e.target)) || (menuRef.current && menuRef.current.contains(e.target))
      if (!inside) setOpen(false)
    }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const choose = preset => { onChange({ preset }); setOpen(false) }
  const applyCustom = () => {
    if (!draft.from && !draft.to) return
    onChange({ preset: 'custom', from: draft.from || draft.to, to: draft.to || draft.from })
    setOpen(false)
  }

  return (
    <Wrap ref={ref}>
      <Trigger type='button' aria-haspopup='dialog' aria-expanded={open} onClick={() => setOpen(o => !o)} title='Choose the reporting period'>
        <DateRangeRoundedIcon style={{ fontSize: 17 }} />
        <TriggerText>
          <small>{label}</small>
          <span>{value?.preset === 'none' ? 'Pick dates' : resolved.label}</span>
        </TriggerText>
        <ArrowDropDownRoundedIcon style={{ fontSize: 20 }} />
      </Trigger>
      {open && (() => {
        const menu = (
        <Menu ref={menuRef} role='dialog' aria-label='Choose the reporting period'>
          <MenuTitle>Quick periods</MenuTitle>
          <PresetGrid>
            {presets.map(p => (
              <Preset key={p.key} type='button' $active={value?.preset === p.key || (!value?.preset && p.key === 'all')} onClick={() => choose(p.key)}>
                {p.label}
              </Preset>
            ))}
          </PresetGrid>
          <MenuTitle>Or pick exact dates</MenuTitle>
          <Custom>
            <Field>
              <span>From</span>
              <input type='date' value={draft.from} max={draft.to || undefined} onChange={e => setDraft(d => ({ ...d, from: e.target.value }))} aria-label='Start date' />
            </Field>
            <Field>
              <span>To</span>
              <input type='date' value={draft.to} min={draft.from || undefined} onChange={e => setDraft(d => ({ ...d, to: e.target.value }))} aria-label='End date' />
            </Field>
          </Custom>
          <Apply type='button' onClick={applyCustom} disabled={!draft.from && !draft.to}>Show these dates</Apply>
        </Menu>
        )
        return isMobile ? createPortal(menu, document.body) : menu
      })()}
    </Wrap>
  )
}

export default ReportPeriodPicker

const Wrap = styled.div` position: relative; display: inline-flex; `

const Trigger = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 8px;
  min-height: 40px; padding: 4px 8px 4px 12px; border-radius: 10px; cursor: pointer;
  background: #fff; border: 1px solid #d0d5dd; color: #101828; text-align: left;
  &:hover { border-color: #33becc; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const TriggerText = styled.span`
  display: inline-flex; flex-direction: column; line-height: 1.2;
  small { font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #667085; }
  span { font-size: 13.5px; font-weight: 700; white-space: nowrap; }
`

const Menu = styled.div`
  position: absolute; top: calc(100% + 6px); left: 0; z-index: 60;
  width: 340px; padding: 14px;
  background: #fff; border: 1px solid #e6e8f0; border-radius: 12px;
  box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12);

  @media (max-width: 768px) {
    position: fixed; left: 16px; right: 16px; top: auto; bottom: 16px; width: auto;
    max-height: 80vh; overflow-y: auto; z-index: 100001;
  }
`

const MenuTitle = styled.div`
  font-size: 11px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase;
  color: #667085; margin: 2px 0 8px;
`

const PresetGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 6px; margin-bottom: 14px;
`

const Preset = styled.button`
  font: inherit; min-height: 40px; padding: 8px 10px; border-radius: 8px; cursor: pointer;
  font-size: 13.5px; font-weight: 600; text-align: left;
  border: 1px solid ${({ $active }) => ($active ? '#33becc' : '#e4e7ec')};
  background: ${({ $active }) => ($active ? '#e6f7f9' : '#fff')};
  color: ${({ $active }) => ($active ? '#1d7f89' : '#344054')};
  &:hover { border-color: #33becc; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const Custom = styled.div` display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 10px; `

const Field = styled.label`
  display: flex; flex-direction: column; gap: 4px;
  span { font-size: 12.5px; font-weight: 700; color: #344054; }
  input {
    font: inherit; font-size: 14px; min-height: 40px; padding: 6px 8px;
    border: 1px solid #d0d5dd; border-radius: 8px; color: #101828; min-width: 0;
  }
  input:focus { outline: 2px solid #33becc; outline-offset: 1px; }
`

const Apply = styled.button`
  font: inherit; width: 100%; min-height: 42px; border-radius: 10px; cursor: pointer;
  border: 1px solid #33becc; background: #33becc; color: #fff; font-weight: 700; font-size: 14px;
  &:hover:not(:disabled) { background: #2aa6b3; }
  &:disabled { opacity: 0.5; cursor: default; }
`
